import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { LayoutDashboard, Store, ListChecks, ClipboardCheck, RotateCcw, Info } from "lucide-react";

type Status = "good" | "warn" | "critical";
interface DemoStore { id: string; name: string; city: string; status: Status; sales: number; target: number; staff: number }
interface DemoAction { id: string; storeId: string; title: string; owner: string | null; state: "open" | "assigned" | "resolved"; note?: string }
interface DemoState { stores: DemoStore[]; actions: DemoAction[]; handoff: string; closed: string[] }

const KEY = "storecentral_demo_state_v1";
const PEOPLE = ["Alex Rivera (sample)", "Jordan Lee (sample)", "Sam Patel (sample)"];

export const initialDemoState = (): DemoState => ({
  stores: [
    { id: "s1", name: "Maple Street (Sample)", city: "Fictionville", status: "good", sales: 18400, target: 17000, staff: 9 },
    { id: "s2", name: "Harbor Point (Sample)", city: "Example Bay", status: "warn", sales: 12100, target: 14500, staff: 7 },
    { id: "s3", name: "Cedar Plaza (Sample)", city: "Demotown", status: "critical", sales: 8300, target: 13000, staff: 5 },
  ],
  actions: [
    { id: "a1", storeId: "s3", title: "Freezer temperature above threshold", owner: null, state: "open" },
    { id: "a2", storeId: "s2", title: "Promo endcap not set up", owner: null, state: "open" },
    { id: "a3", storeId: "s1", title: "Weekly safety walk", owner: PEOPLE[0], state: "resolved", note: "Completed, no issues" },
  ],
  handoff: "",
  closed: [],
});

const load = (): DemoState => {
  try { const raw = sessionStorage.getItem(KEY); if (raw) return JSON.parse(raw); } catch { /* ignore */ }
  return initialDemoState();
};

const useDemoState = () => {
  const [state, setState] = useState<DemoState>(load);
  useEffect(() => { sessionStorage.setItem(KEY, JSON.stringify(state)); }, [state]);
  const reset = () => { sessionStorage.removeItem(KEY); setState(initialDemoState()); };
  return { state, setState, reset };
};

const PILL: Record<string, string> = {
  good: "bg-status-good/12 text-status-good ring-status-good/25",
  warn: "bg-status-warn/12 text-status-warn ring-status-warn/25",
  critical: "bg-status-critical/12 text-status-critical ring-status-critical/25",
  open: "bg-status-critical/12 text-status-critical ring-status-critical/25",
  assigned: "bg-status-warn/12 text-status-warn ring-status-warn/25",
  resolved: "bg-status-good/12 text-status-good ring-status-good/25",
};
const Pill: React.FC<{ s: string; testId?: string }> = ({ s, testId }) => (
  <span data-testid={testId} className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium capitalize ring-1 ring-inset ${PILL[s]}`}>{s}</span>
);
const Card: React.FC<{ children: React.ReactNode; className?: string; testId?: string }> = ({ children, className = "", testId }) => (
  <div data-testid={testId} className={`rounded-xl border bg-card p-4 ${className}`}>{children}</div>
);
const money = (n: number) => `$${n.toLocaleString("en-US")}`;

const NAV = [
  { to: "/demo", label: "Today", icon: LayoutDashboard, id: "today" },
  { to: "/demo/stores", label: "Stores", icon: Store, id: "stores" },
  { to: "/demo/actions", label: "Actions", icon: ListChecks, id: "actions" },
  { to: "/demo/handoff", label: "Handoff", icon: ClipboardCheck, id: "handoff" },
];

const Banner: React.FC<{ onReset: () => void }> = ({ onReset }) => (
  <div data-testid="demo-banner" className="flex flex-wrap items-center justify-between gap-2 bg-brand px-4 py-2 text-sm text-white">
    <span className="flex items-center gap-2"><Info className="h-4 w-4" />Sample demo — fictional data. Your changes stay in this browser tab only (session storage); no real notifications are sent. The preview host may load its own analytics scripts.</span>
    <Button size="sm" variant="secondary" onClick={onReset} data-testid="demo-reset-btn"><RotateCcw className="mr-1 h-3.5 w-3.5" />Reset demo</Button>
  </div>
);

const Nav: React.FC<{ page: string }> = ({ page }) => (
  <nav data-testid="demo-nav" className="fixed inset-x-0 bottom-0 z-20 flex border-t bg-card md:static md:min-h-[calc(100vh-40px)] md:w-52 md:flex-col md:border-r md:border-t-0 md:p-3">
    <div className="hidden px-2 pb-4 font-display text-lg font-bold md:block">StoreCentral <span className="text-xs font-normal text-muted-foreground">demo</span></div>
    {NAV.map((n) => (
      <Link key={n.id} to={n.to} data-testid={`demo-nav-${n.id}`}
        className={`flex flex-1 flex-col items-center gap-1 px-2 py-2 text-xs md:flex-none md:flex-row md:gap-2 md:rounded-lg md:text-sm ${page === n.id ? "text-brand md:bg-brand/10" : "text-muted-foreground"}`}>
        <n.icon className="h-4 w-4" />{n.label}
      </Link>
    ))}
  </nav>
);

const Today: React.FC<{ s: DemoState }> = ({ s }) => {
  const open = s.actions.filter((a) => a.state !== "resolved").length;
  const sales = s.stores.reduce((t, x) => t + x.sales, 0);
  return (
    <div data-testid="demo-today">
      <h1 className="mb-4 font-display text-2xl font-bold">Today overview</h1>
      <div className="mb-6 grid gap-3 sm:grid-cols-3">
        <Card testId="demo-kpi-stores"><div className="text-xs text-muted-foreground">Stores</div><div className="text-2xl font-bold">{s.stores.length}</div></Card>
        <Card testId="demo-kpi-sales"><div className="text-xs text-muted-foreground">Sales today</div><div className="text-2xl font-bold">{money(sales)}</div></Card>
        <Card testId="demo-kpi-open"><div className="text-xs text-muted-foreground">Open actions</div><div className="text-2xl font-bold">{open}</div></Card>
      </div>
      <StoreList s={s} />
    </div>
  );
};

const StoreList: React.FC<{ s: DemoState }> = ({ s }) => (
  <div className="grid gap-3" data-testid="demo-store-list">
    {s.stores.map((st) => (
      <Link key={st.id} to={`/demo/stores/${st.id}`} data-testid={`demo-store-${st.id}`}>
        <Card className="flex items-center justify-between hover:border-brand">
          <div><div className="font-medium">{st.name}</div><div className="text-xs text-muted-foreground">{st.city} · {money(st.sales)} / {money(st.target)}</div></div>
          <Pill s={st.status} />
        </Card>
      </Link>
    ))}
  </div>
);

const StoreDetail: React.FC<{ s: DemoState; id: string }> = ({ s, id }) => {
  const st = s.stores.find((x) => x.id === id);
  if (!st) return <p data-testid="demo-store-missing">Sample store not found. <Link className="text-brand" to="/demo/stores">Back to stores</Link></p>;
  const acts = s.actions.filter((a) => a.storeId === id);
  return (
    <div data-testid="demo-store-detail">
      <Link to="/demo/stores" className="text-sm text-brand" data-testid="demo-back-stores">← Stores</Link>
      <div className="my-3 flex items-center gap-3"><h1 className="font-display text-2xl font-bold">{st.name}</h1><Pill s={st.status} /></div>
      <div className="mb-6 grid gap-3 sm:grid-cols-3">
        <Card><div className="text-xs text-muted-foreground">Sales vs target</div><div className="text-xl font-bold">{Math.round((st.sales / st.target) * 100)}%</div></Card>
        <Card><div className="text-xs text-muted-foreground">Staff on shift</div><div className="text-xl font-bold">{st.staff}</div></Card>
        <Card><div className="text-xs text-muted-foreground">Store day</div><div className="text-xl font-bold">{s.closed.includes(id) ? "Closed" : "Open"}</div></Card>
      </div>
      <h2 className="mb-2 font-semibold">Actions for this store</h2>
      <ActionList s={s} items={acts} />
    </div>
  );
};

const ActionList: React.FC<{ s: DemoState; items: DemoAction[] }> = ({ s, items }) => (
  <div className="grid gap-3">
    {items.map((a) => (
      <Link key={a.id} to={`/demo/actions/${a.id}`} data-testid={`demo-action-${a.id}`}>
        <Card className="flex items-center justify-between hover:border-brand">
          <div><div className="font-medium">{a.title}</div><div className="text-xs text-muted-foreground">{s.stores.find((x) => x.id === a.storeId)?.name} · {a.owner || "Unassigned"}</div></div>
          <Pill s={a.state} />
        </Card>
      </Link>
    ))}
  </div>
);

const ActionDetail: React.FC<{ s: DemoState; id: string; update: (a: DemoAction) => void }> = ({ s, id, update }) => {
  const a = s.actions.find((x) => x.id === id);
  const [owner, setOwner] = useState(a?.owner || PEOPLE[1]);
  const [note, setNote] = useState("");
  if (!a) return <p data-testid="demo-action-missing">Sample action not found.</p>;
  return (
    <div data-testid="demo-action-detail" className="max-w-xl">
      <Link to="/demo/actions" className="text-sm text-brand">← Actions</Link>
      <div className="my-3 flex items-center gap-3"><h1 className="font-display text-xl font-bold">{a.title}</h1><Pill s={a.state} testId="demo-action-state" /></div>
      <p className="mb-4 text-sm text-muted-foreground">Owner: <span data-testid="demo-action-owner">{a.owner || "Unassigned"}</span>{a.note && <> · Resolution: <span data-testid="demo-action-note">{a.note}</span></>}</p>
      {a.state === "open" && (
        <Card className="grid gap-2">
          <label className="text-sm font-medium" htmlFor="demo-owner">Assign to</label>
          <select id="demo-owner" data-testid="demo-assign-select" className="rounded-md border bg-background p-2 text-sm" value={owner} onChange={(e) => setOwner(e.target.value)}>
            {PEOPLE.map((p) => <option key={p}>{p}</option>)}
          </select>
          <Button data-testid="demo-assign-btn" onClick={() => update({ ...a, owner, state: "assigned" })}>Assign</Button>
        </Card>
      )}
      {a.state === "assigned" && (
        <Card className="grid gap-2">
          <label className="text-sm font-medium" htmlFor="demo-note">Resolution note</label>
          <textarea id="demo-note" data-testid="demo-resolve-note" className="rounded-md border bg-background p-2 text-sm" value={note} onChange={(e) => setNote(e.target.value)} placeholder="What was done?" />
          <Button data-testid="demo-resolve-btn" disabled={!note.trim()} onClick={() => update({ ...a, state: "resolved", note: note.trim() })}>Mark resolved</Button>
        </Card>
      )}
      <Button variant="outline" className="mt-4" disabled data-testid="demo-notify-unavailable">Notify owner (unavailable in demo)</Button>
    </div>
  );
};

const Handoff: React.FC<{ s: DemoState; setState: React.Dispatch<React.SetStateAction<DemoState>> }> = ({ s, setState }) => {
  const [text, setText] = useState(s.handoff);
  const resolved = s.actions.filter((a) => a.state === "resolved");
  const open = s.actions.filter((a) => a.state !== "resolved");
  const done = s.closed.length === s.stores.length;
  return (
    <div data-testid="demo-handoff" className="max-w-2xl">
      <h1 className="mb-4 font-display text-2xl font-bold">Shift handoff & results</h1>
      <div className="mb-4 grid gap-3 sm:grid-cols-2">
        <Card testId="demo-results-resolved"><div className="text-xs text-muted-foreground">Resolved today</div><div className="text-2xl font-bold">{resolved.length}</div></Card>
        <Card testId="demo-results-open"><div className="text-xs text-muted-foreground">Carry over to next shift</div><div className="text-2xl font-bold">{open.length}</div></Card>
      </div>
      <Card className="grid gap-2">
        <label className="text-sm font-medium" htmlFor="demo-handoff-text">Next-shift priorities</label>
        <textarea id="demo-handoff-text" data-testid="demo-handoff-input" className="min-h-24 rounded-md border bg-background p-2 text-sm" value={text} onChange={(e) => setText(e.target.value)} />
        <Button data-testid="demo-close-day-btn" disabled={done}
          onClick={() => setState((p) => ({ ...p, handoff: text, closed: p.stores.map((x) => x.id) }))}>
          {done ? "Store days closed" : "Close store days & hand off"}
        </Button>
        {done && <p data-testid="demo-handoff-saved" className="text-sm text-status-good">Handoff recorded in this browser tab only: “{s.handoff || "No notes"}”</p>}
      </Card>
    </div>
  );
};

export default function Demo() {
  const { section, id } = useParams();
  const navigate = useNavigate();
  const { state, setState, reset } = useDemoState();
  const page = section || "today";
  const update = (a: DemoAction) => setState((p) => ({ ...p, actions: p.actions.map((x) => (x.id === a.id ? a : x)) }));
  useEffect(() => { document.title = "StoreCentral — Sample demo"; }, []);
  const body = (() => {
    if (page === "stores") return id ? <StoreDetail s={state} id={id} /> : <><h1 className="mb-4 font-display text-2xl font-bold">Stores</h1><StoreList s={state} /></>;
    if (page === "actions") return id ? <ActionDetail key={id} s={state} id={id} update={update} /> : <><h1 className="mb-4 font-display text-2xl font-bold">Actions</h1><ActionList s={state} items={state.actions} /></>;
    if (page === "handoff") return <Handoff key={state.closed.length + state.handoff} s={state} setState={setState} />;
    return <Today s={state} />;
  })();
  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="demo-root">
      <Banner onReset={() => { reset(); navigate("/demo"); }} />
      <div className="md:flex">
        <Nav page={page} />
        <main className="flex-1 p-4 pb-24 md:p-8">{body}</main>
      </div>
    </div>
  );
}
